/**
 * Command palette (⌘K).
 *
 * ERP-UX.md §2 lo llama «el acelerador principal», y no exagera: en un ERP
 * con veinte pantallas, llegar a la ficha de un extintor por el menú son
 * cuatro clics y un filtro; aquí son cuatro letras.
 *
 * Hace dos cosas a la vez, y por eso no es sólo un buscador:
 *
 * - **Encuentra**: clientes, centros, equipos, órdenes, facturas y
 *   presupuestos. Lo que el servidor deja ver, ni más ni menos.
 * - **Ejecuta**: «nuevo cliente», «ir a facturas». Las acciones se filtran
 *   por permiso, igual que el menú.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hasPermission, useSession } from '../session/session-context.js';
import { NAVIGATION } from './navigation.js';

interface SearchHit {
  type: 'CUSTOMER' | 'SITE' | 'EQUIPMENT' | 'WORK_ORDER' | 'INVOICE' | 'QUOTE';
  id: string;
  title: string;
  subtitle: string | null;
  code: string;
}

const TYPE_LABEL: Record<SearchHit['type'], string> = {
  CUSTOMER: 'Cliente',
  SITE: 'Centro',
  EQUIPMENT: 'Equipo',
  WORK_ORDER: 'Orden',
  INVOICE: 'Factura',
  QUOTE: 'Presupuesto',
};

/**
 * A dónde lleva cada resultado.
 *
 * Lo que todavía no tiene ficha propia lleva a su listado **con el término
 * buscado puesto**: es peor caer en un listado genérico que en uno ya
 * filtrado por lo que la persona escribió.
 */
function destinationOf(hit: SearchHit): string {
  switch (hit.type) {
    case 'CUSTOMER':
      return `/clientes/${hit.id}`;
    case 'SITE':
      return `/centros/${hit.id}`;
    case 'EQUIPMENT':
      return `/equipos?q=${encodeURIComponent(hit.code)}`;
    case 'WORK_ORDER':
      return `/ordenes?q=${encodeURIComponent(hit.code)}&v=all`;
    case 'INVOICE':
      return `/facturas?q=${encodeURIComponent(hit.code)}`;
    case 'QUOTE':
      return `/presupuestos?q=${encodeURIComponent(hit.code)}&v=all`;
  }
}

interface Command {
  id: string;
  label: string;
  hint: string;
  to: string;
  permission: string;
}

const COMMANDS: readonly Command[] = [
  { id: 'new-customer', label: 'Nuevo cliente', hint: 'Alta', to: '/clientes/nuevo', permission: 'customers.create' },
  { id: 'new-site', label: 'Nuevo centro', hint: 'Alta', to: '/centros/nuevo', permission: 'customers.create' },
  { id: 'new-equipment', label: 'Nuevo equipo', hint: 'Alta', to: '/equipos/nuevo', permission: 'assets.create' },
  { id: 'new-contract', label: 'Nuevo contrato', hint: 'Alta', to: '/contratos/nuevo', permission: 'maintenance.create' },
  { id: 'new-workorder', label: 'Nueva orden de trabajo', hint: 'Alta', to: '/ordenes/nueva', permission: 'fieldservice.create' },
  { id: 'new-quote', label: 'Nuevo presupuesto', hint: 'Alta', to: '/presupuestos/nuevo', permission: 'sales.create' },
  { id: 'new-invoice', label: 'Nueva factura', hint: 'Alta', to: '/facturas/nueva', permission: 'billing.create' },
  { id: 'settings', label: 'Configuración de la empresa', hint: 'Ir a', to: '/configuracion', permission: 'platform.read' },
  { id: 'overdue', label: 'Equipos vencidos', hint: 'Vista', to: '/equipos?v=overdue', permission: 'assets.read' },
  { id: 'breached', label: 'Órdenes fuera de plazo', hint: 'Vista', to: '/ordenes?v=breached', permission: 'fieldservice.read' },
  { id: 'nocontract', label: 'Centros sin contrato', hint: 'Vista', to: '/planificacion?v=nocontract', permission: 'maintenance.read' },
  { id: 'unpaid', label: 'Facturas vencidas', hint: 'Vista', to: '/facturas?v=overdue', permission: 'billing.read' },
  { id: 'une', label: 'Reglas que dependen de una UNE', hint: 'Vista', to: '/normativa?v=une', permission: 'regulation.read' },
];

export function CommandPalette(): React.ReactNode {
  const { api, user } = useSession();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [active, setActive] = useState(0);
  const [searching, setSearching] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const request = useRef(0);

  // ⌘K en Mac, Ctrl+K en el resto. También se cierra con Escape.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((previous) => !previous);
        return;
      }
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery('');
      setHits([]);
      setActive(0);
      // El foco va al campo en cuanto se abre: si hubiera que hacer clic,
      // el atajo no ahorraría nada.
      window.setTimeout(() => input.current?.focus(), 0);
    }
  }, [open]);

  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setHits([]);
      return;
    }

    const ticket = ++request.current;
    setSearching(true);

    const timer = setTimeout(() => {
      api
        .get<{ results: SearchHit[] }>(`/search?q=${encodeURIComponent(term)}&limit=12`)
        .then((data) => {
          // Una respuesta de una búsqueda ya superada pintaría resultados
          // que no corresponden a lo que hay escrito.
          if (ticket === request.current) setHits(data.results ?? []);
        })
        .catch(() => {
          if (ticket === request.current) setHits([]);
        })
        .finally(() => {
          if (ticket === request.current) setSearching(false);
        });
    }, 180);

    return () => clearTimeout(timer);
  }, [api, query]);

  const commands = COMMANDS.filter(
    (command) =>
      hasPermission(user, command.permission) &&
      (query.trim() === '' || command.label.toLowerCase().includes(query.trim().toLowerCase())),
  );

  const sections = NAVIGATION.flatMap((group) =>
    group.items
      .filter(
        (item) =>
          hasPermission(user, item.permission) &&
          query.trim() !== '' &&
          item.label.toLowerCase().includes(query.trim().toLowerCase()),
      )
      .map((item) => ({ id: `nav-${item.path}`, label: item.label, hint: group.label, to: item.path })),
  );

  const entries: Array<{ id: string; label: string; hint: string; to: string }> = [
    ...hits.map((hit) => ({
      id: `${hit.type}-${hit.id}`,
      label: `${hit.title}${hit.code && hit.code !== hit.title ? ` · ${hit.code}` : ''}`,
      hint: `${TYPE_LABEL[hit.type]}${hit.subtitle ? ` · ${hit.subtitle}` : ''}`,
      to: destinationOf(hit),
    })),
    ...sections,
    ...commands.map((c) => ({ id: c.id, label: c.label, hint: c.hint, to: c.to })),
  ];

  const go = useCallback(
    (to: string) => {
      setOpen(false);
      void navigate(to);
    },
    [navigate],
  );

  function onInputKey(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((i) => Math.min(i + 1, Math.max(entries.length - 1, 0)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const entry = entries[active];
      if (entry) go(entry.to);
    }
  }

  if (!open) return null;

  return (
    <div className="palette" role="dialog" aria-modal="true" aria-label="Buscar y ejecutar">
      <div className="palette__backdrop" onClick={() => setOpen(false)} />
      <div className="palette__panel">
        <input
          ref={input}
          className="palette__input"
          type="search"
          value={query}
          placeholder="Buscar cliente, centro, equipo, OT, factura… o escribir una acción"
          aria-label="Buscar"
          aria-expanded
          aria-controls="palette-results"
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
          }}
          onKeyDown={onInputKey}
        />

        <ul className="palette__results" id="palette-results" role="listbox">
          {entries.map((entry, index) => (
            <li key={entry.id}>
              <button
                type="button"
                role="option"
                aria-selected={index === active}
                className={`palette__item${index === active ? ' palette__item--on' : ''}`}
                onMouseEnter={() => setActive(index)}
                onClick={() => go(entry.to)}
              >
                <span className="palette__label">{entry.label}</span>
                <span className="palette__hint">{entry.hint}</span>
              </button>
            </li>
          ))}

          {entries.length === 0 && (
            <li className="palette__empty">
              {query.trim().length < 2
                ? 'Escriba al menos dos letras.'
                : searching
                  ? 'Buscando…'
                  : 'Nada coincide. Sólo se busca en lo que su perfil puede ver.'}
            </li>
          )}
        </ul>

        <footer className="palette__foot">
          <span>↑↓ moverse</span>
          <span>↵ abrir</span>
          <span>Esc cerrar</span>
        </footer>
      </div>
    </div>
  );
}
